// Single source of truth for "scoped writes not yet confirmed by the server".
//
// This replaces the old fire-and-forget localStorage write-through for first-class
// data: putScoped()/removeScoped() are AWAITED, confirmed, and retried — the same
// reliable shape the MLS/org/agent stores already use (which is why those always
// saved on iPhone and contacts didn't). The pending set drives the on-screen save
// indicator, and flushBeacon() is the unload safety net for anything still in flight.

import { api } from '../api';

const API = process.env.REACT_APP_API_URL || '';
const pending = new Map();   // key -> { data } | { del: true }

function emit() {
  try { window.dispatchEvent(new CustomEvent('ira:sync', { detail: { pending: pending.size } })); } catch { /* ignore */ }
}
export function pendingCount() { return pending.size; }

// Await a confirmed save of one scoped doc. Retries with backoff; the entry stays in
// `pending` (→ "Saving…/Not synced") until the server confirms. A newer save for the
// same key supersedes this one (prevents a stale retry from clobbering fresh data).
export async function putScoped(key, data) {
  pending.set(key, { data });
  emit();
  for (let attempt = 0; ; attempt++) {
    const cur = pending.get(key);
    if (!cur || cur.del || cur.data !== data) return false;   // superseded by a newer write
    try {
      await api.put('/api/store', { key, data });
      const now = pending.get(key);
      if (now && !now.del && now.data === data) { pending.delete(key); emit(); }
      return true;
    } catch {
      if (attempt >= 3) return false;   // give up the active loop; stays pending → beacon/next save covers it
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
}

export async function removeScoped(key) {
  pending.set(key, { del: true });
  emit();
  for (let attempt = 0; ; attempt++) {
    const cur = pending.get(key);
    if (!cur || !cur.del) return false;   // superseded by a write
    try {
      await api.delete(`/api/store/${encodeURIComponent(key)}`);
      if (pending.get(key)?.del) { pending.delete(key); emit(); }
      return true;
    } catch {
      if (attempt >= 3) return false;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
}

// Fire-and-forget mirror for non-critical keys (run history, archives, etc.) that
// don't warrant an awaited call but should still reach the server.
export function mirror(key, rawValue) {
  let data; try { data = JSON.parse(rawValue); } catch { data = rawValue; }
  putScoped(key, data);
}
export function mirrorRemove(key) { removeScoped(key); }

// Resolve once the pending set drains (or a timeout). Lets a discrete action confirm
// its save reached the DB before telling the user (e.g. the reminder editor's ✓ Saved).
export async function flush(timeoutMs = 8000) {
  const start = Date.now();
  while (pending.size > 0 && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 150));
  }
  return pending.size === 0;
}

// Unload safety net: beacon everything still unconfirmed in ONE request. sendBeacon
// is the transport iOS Safari can't drop on a background/close.
export function flushBeacon() {
  if (pending.size === 0) return true;
  const writes = [], deletes = [];
  for (const [key, p] of pending) { if (p.del) deletes.push(key); else writes.push({ key, data: p.data }); }
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ writes, deletes })], { type: 'application/json' });
      if (navigator.sendBeacon(`${API}/api/store/flush`, blob)) { pending.clear(); emit(); return true; }
    }
  } catch { /* fall through */ }
  return false;
}
